import { Box } from '@abyss/web/ui/Box';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import find from 'lodash/find';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ImageComponent } from '../../../../common/ImageComponent';
import {
  SmartChoiceScore,
  SmartChoiceScoreBox,
} from '../../../../common/SnackCard/SnackCard.styles';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { ProviderType } from '../../../../models/ProviderDetails';
import {
  getNameAttrs,
  parseProviderName,
} from '../../../../utils/providerNames.utils';
import { AvatarCompareCard } from '../MapView/CompareDrawer/utility/compareDrawer.styled';
import {
  DividerStyle,
  LayoutGroupStyled,
  ProviderChangeButton,
  ProviderNameLinkStyle,
  ProviderNameWithScore,
  ProviderNameWrapper,
} from './CompareSmartChoice.styled';
import { isAttributeChecked } from './utils';

export const CompareHeader = ({
  imgRef,
  onProviderClickHandler,
  selectedAttributes,
  selectedProviders,
  setIsPickProviderModalOpen,
  setPickProviderIndex,
  isChangeBtnVisible = false,
  filteredProviders,
}) => {
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;
  const lagoonData = useLagoon('ui-messaging')();
  const { t } = useTranslation();
  const no_specialty = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_SPECIALTY_NOT_AVAILABLE,
  })?.message;
  const pcp_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_PCP_SERVICES_AVAILABLE,
  })?.message;
  const pcp_not_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_PCP_SERVICES_NOT_AVAILABLE,
  })?.message;
  const [removeTruncatedDataCard] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_TRUNCATION_DATACARD,
  ]);
  return (
    <Layout.Stack grow style={{ width: '100%' }}>
      <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
        {filteredProviders?.map((provider, index) => (
          <Box
            color="$white"
            css={{
              padding: '8px',
              border: '1px solid rgba(229, 229, 230, 1)',
              boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.05)',
              minHeight: removeTruncatedDataCard
                ? selectedProviders > 3
                  ? '160px'
                  : isSmartChoice === false
                  ? 'auto'
                  : '95px'
                : 'auto',
            }}
            key={index}
          >
            <LayoutGroupStyled
              className="icon-name-container"
              cssProps={{ count: selectedProviders }}
            >
              <AvatarCompareCard>
                {ImageComponent({
                  imgRef,
                  providerType: provider?.providerType,
                  imgSrc: provider?.imageLocation,
                  sizeToken: '$compareAndShareIconSize',
                })}
              </AvatarCompareCard>
              <ProviderNameWithScore
                cssProps={{ count: selectedProviders, removeTruncatedDataCard }}
              >
                <ProviderNameWrapper
                  cssProps={{
                    count: selectedProviders,
                    removeTruncatedDataCard,
                  }}
                >
                  <ProviderNameWithTooltip
                    handleProviderClick={() => {
                      adobeLinkTrackEvent({
                        name: 'Provider name',
                        location: 'Compare providers successful',
                        type: 'Internal',
                      });
                      onProviderClickHandler(provider);
                    }}
                    provider={provider}
                    selectedProviders={selectedProviders}
                  />
                </ProviderNameWrapper>
                {isSmartChoice && (
                  <SmartChoiceScoreBox
                    css={{ borderRadius: '4px' }}
                    data-testid={`compare-header-smart-choice-${index}`}
                  >
                    <SmartChoiceScore>
                      {`${t('SMART_CHOICE.SMART_CHOICE')} ${
                        provider?.recommendationDetails
                          ?.totalRecommendationScore || 0
                      }%`}
                    </SmartChoiceScore>
                  </SmartChoiceScoreBox>
                )}
              </ProviderNameWithScore>
            </LayoutGroupStyled>
            {isChangeBtnVisible && (
              <ProviderChangeButton
                data-testid="provider-change-button"
                onClick={() => {
                  setIsPickProviderModalOpen(true);
                  setPickProviderIndex(index);
                }}
              >
                <IconSymbol icon="swap_vert" />
                <Text color="#196ECF" size={14}>
                  {t('Change')}
                </Text>
                <IconSymbol icon="expand_more" />
              </ProviderChangeButton>
            )}
          </Box>
        ))}
      </LayoutGroupStyled>
      {selectedAttributes?.find((item) => item?.name === 'specialties')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                css={{ marginTop: '16px' }}
                data-testid="provider-specialty"
                key={index}
              >
                <Text>
                  {provider?.speciality !== ''
                    ? provider?.speciality
                    : no_specialty}
                </Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          {isSmartChoice &&
            isAttributeChecked(selectedAttributes, [
              'hasPCPServiceAvailability',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          {!isSmartChoice && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {selectedAttributes?.find(
        (item) => item?.name === 'hasPCPServiceAvailability'
      )?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                data-testid="provider-availability-available"
                key={index}
              >
                {provider?.hasPCPServiceAvailability && (
                  <IconSymbol color="#007000" icon="done" size={20} />
                )}
                <Text>
                  {provider?.hasPCPServiceAvailability
                    ? pcp_available
                    : pcp_not_available}
                </Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {!isSmartChoice && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
    </Layout.Stack>
  );
};

export const ProviderNameWithTooltip = ({
  provider,
  handleProviderClick,
  selectedProviders,
}) => {
  const nameAttrs = getNameAttrs(provider);
  const [removeTruncatedDataCard] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_TRUNCATION_DATACARD,
  ]);
  return (
    <Tooltip
      align="center"
      asChild={false}
      content={parseProviderName(
        provider?.providerName,
        ProviderType.PRACTITIONER,
        provider?.primaryDegrees?.[0],
        nameAttrs
      )}
      data-testid="provider-card-detail"
      position="top"
      positionOffset={8}
    >
      <Text
        css={{
          ...ProviderNameLinkStyle(selectedProviders, removeTruncatedDataCard),
        }}
        data-auto-testid={`provider-card-detail-button-compare-${provider?.providerId}`}
        data-testid={`provider-card-detail-button-compare-${provider?.providerId}`}
        onClick={handleProviderClick}
      >
        {parseProviderName(
          provider?.providerName,
          ProviderType.PRACTITIONER,
          provider?.primaryDegrees?.[0],
          nameAttrs
        )}
      </Text>
    </Tooltip>
  );
};
